/**
 * usePermissions — primary hook for permission-based UI gating.
 *
 * All visibility and access checks in the UI should use this hook,
 * never check system_role directly.
 *
 * Usage:
 *   const { hasPermission, hasAnyPermission } = usePermissions();
 *   if (hasPermission(Permissions.PLATFORM_SETTINGS_VIEW)) { ... }
 */
import { useMemo } from 'react';
import { useOrgStore } from '../stores/orgStore';
import { Permissions, type Permission } from '../config/permissions';

export function usePermissions() {
  const userContext = useOrgStore((s) => s.userContext);

  const permissionSet = useMemo(
    () => new Set<string>(userContext?.permissions ?? []),
    [userContext?.permissions],
  );

  const hasPermission = (perm: Permission): boolean => permissionSet.has(perm);

  const hasAnyPermission = (...perms: Permission[]): boolean =>
    perms.some((p) => permissionSet.has(p));

  const hasAllPermissions = (...perms: Permission[]): boolean =>
    perms.every((p) => permissionSet.has(p));

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    permissions: userContext?.permissions ?? [],
    Permissions,
  };
}

export { Permissions } from '../config/permissions';
export type { Permission } from '../config/permissions';
